import { startCase } from 'es-toolkit';
import React, { useState } from 'react';
import { Box } from '@mui/material';

import { PropertySelectField, PropertyTextInput } from '@paragon/client-tools';
import {
  Component,
  CoordinateAxis,
  Distribution,
  ManualDistributedLoadDefinition,
  ManualLoadDefinition,
} from '@paragon/contract-library';
import { loadMemberTypes } from '@paragon/contract-library/lib/designService';
import { distributedForceEquals, stressEquals } from '@paragon/geometry-library';
import { DistanceField } from '@paragon/material';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { distributedManualLoadDefinitionHasDifferentStartAndEnd } from '../insertions/manualLoadValidation';
import { LoadDirectionSelect } from './LoadDirectionSelect';
import { LoadDistribution, LoadDistributionSelect } from './LoadDistributionSelect';
import { LoadMagnitudeSettings } from './LoadMagnitudeSettings';
import { LoadTypeSelect } from './LoadTypeSelect';

const definitionAxisOptions: CoordinateAxis[] = ['X', 'Y'];

interface ManualLoadProjectedCoordinatesFieldsProps {
  readonly manualLoadDefinition: ManualLoadDefinition;
  readonly onUpdate: (updatedManualLoadDefinition: ManualLoadDefinition) => void;
  readonly component: Component;
  readonly coordinateIsOnComponent: (definitionAxis: CoordinateAxis, coordinate: number) => boolean;
  readonly canSubmitWhenInvalid?: boolean;
  readonly disabled?: boolean;
}

function isUniform(manualLoadDefinition: ManualDistributedLoadDefinition) {
  switch (manualLoadDefinition.magnitudeUnit) {
    case 'PoundsPerLinearFoot':
      return distributedForceEquals(manualLoadDefinition.startMagnitude, manualLoadDefinition.endMagnitude);
    case 'PoundsPerSquareFoot':
      return stressEquals(manualLoadDefinition.startMagnitude, manualLoadDefinition.endMagnitude);
    default:
      assertExhaustiveSwitch(manualLoadDefinition.magnitudeUnit);
  }
}

function getLoadDistribution(manualLoadDefinition: ManualLoadDefinition): LoadDistribution {
  return manualLoadDefinition.distribution === 'Concentrated'
    ? 'Concentrated'
    : isUniform(manualLoadDefinition)
      ? 'Uniform'
      : 'Trapezoidal';
}

export function ManualLoadProjectedCoordinatesFields(props: ManualLoadProjectedCoordinatesFieldsProps) {
  const { component, disabled, manualLoadDefinition, onUpdate, coordinateIsOnComponent, canSubmitWhenInvalid } = props;

  const defaultLoadDistribution = getLoadDistribution(manualLoadDefinition);
  const [loadDistribution, setLoadDistribution] = useState<LoadDistribution>(defaultLoadDistribution);
  function updateDistribution(updatedLoadDistribution: LoadDistribution) {
    setLoadDistribution(updatedLoadDistribution);

    const newDistribution: Distribution = updatedLoadDistribution === 'Concentrated' ? 'Concentrated' : 'Distributed';
    let updatedLoadDefinition: ManualLoadDefinition;
    if (newDistribution === 'Concentrated') {
      // Switching from distributed to concentrated, unless the type is already `Concentrated`, in which case we do not
      // need to take any action.
      if (manualLoadDefinition.distribution === 'Concentrated') return;

      updatedLoadDefinition = {
        distribution: newDistribution,

        // Best match properties
        coordinate: manualLoadDefinition.startCoordinate,
        magnitude: Math.max(manualLoadDefinition.startMagnitude, manualLoadDefinition.endMagnitude),

        // One-to-one copied properties
        // Why not use the spread operator? Using the spread operator would create an object with properties for
        // both types (concentrated and distributed). While the server is able to discriminate because the
        // distribution property is the deserialization Discriminant in C#, setting only the appropriate properties
        // explicitly here makes for a cleaner and less confusing payload and Redux store.
        definitionAxis: manualLoadDefinition.definitionAxis,
        guid: manualLoadDefinition.guid,
        loadDirection: manualLoadDefinition.loadDirection,
        loadType: manualLoadDefinition.loadType,
        memberType: manualLoadDefinition.memberType,
        name: manualLoadDefinition.name,
      };
    } else {
      // Unlike the `if` block above, there is no return statement here. That is because the user may be switching
      // between `Uniform` and `Trapezoidal`, both of which are `Distributed` underneath. When making the switch from
      // `Trapezoidal` to `Uniform`, we will take the higher of the two magnitudes as the `Uniform` magnitude.
      updatedLoadDefinition = {
        distribution: newDistribution,

        // Best match properties
        startCoordinate:
          manualLoadDefinition.distribution === 'Concentrated'
            ? manualLoadDefinition.coordinate
            : manualLoadDefinition.startCoordinate,
        endCoordinate:
          manualLoadDefinition.distribution === 'Concentrated'
            ? manualLoadDefinition.coordinate
            : manualLoadDefinition.endCoordinate,
        startMagnitude:
          manualLoadDefinition.distribution === 'Concentrated'
            ? manualLoadDefinition.magnitude
            : Math.max(manualLoadDefinition.startMagnitude, manualLoadDefinition.endMagnitude),
        endMagnitude:
          manualLoadDefinition.distribution === 'Concentrated'
            ? manualLoadDefinition.magnitude
            : Math.max(manualLoadDefinition.startMagnitude, manualLoadDefinition.endMagnitude),
        magnitudeUnit: 'PoundsPerSquareFoot',
        isProjected: manualLoadDefinition.distribution === 'Distributed' && manualLoadDefinition.isProjected,

        // One-to-one copied properties
        // Why not use the spread operator? Using the spread operator would create an object with properties for
        // both types (concentrated and distributed). While the server is able to discriminate because the
        // distribution property is the deserialization Discriminant in C#, setting only the appropriate properties
        // explicitly here makes for a cleaner and less confusing payload and Redux store.
        definitionAxis: manualLoadDefinition.definitionAxis,
        guid: manualLoadDefinition.guid,
        loadDirection: manualLoadDefinition.loadDirection,
        loadType: manualLoadDefinition.loadType,
        memberType: manualLoadDefinition.memberType,
        name: manualLoadDefinition.name,
      };
    }
    onUpdate(updatedLoadDefinition);
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <LoadDistributionSelect
          sx={{ m: 1, flex: 1 }}
          loadDistribution={loadDistribution}
          submit={updateDistribution}
          disabled={disabled}
        />
        <PropertySelectField
          sx={{ m: 1, flex: 1 }}
          property="Coordinate Axis"
          value={manualLoadDefinition.definitionAxis}
          options={definitionAxisOptions}
          submit={(definitionAxis) => onUpdate({ ...manualLoadDefinition, definitionAxis })}
          disabled={disabled}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <LoadTypeSelect
          sx={{ m: 1, flex: 1 }}
          loadType={manualLoadDefinition.loadType}
          submit={(loadType) => onUpdate({ ...manualLoadDefinition, loadType })}
          disabled={disabled}
        />
        <LoadDirectionSelect
          sx={{ m: 1, flex: 1 }}
          loadDirection={manualLoadDefinition.loadDirection}
          submit={(loadDirection) => onUpdate({ ...manualLoadDefinition, loadDirection })}
          disabled={disabled}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <PropertySelectField
          sx={{ m: 1, flex: 1 }}
          property="Member Type"
          value={manualLoadDefinition.memberType}
          options={loadMemberTypes}
          getName={startCase}
          submit={(memberType) => onUpdate({ ...manualLoadDefinition, memberType })}
          disabled={disabled}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <DistanceField
          label={manualLoadDefinition.distribution === 'Concentrated' ? 'Location' : 'Start Location'}
          value={
            manualLoadDefinition.distribution === 'Concentrated'
              ? manualLoadDefinition.coordinate
              : manualLoadDefinition.startCoordinate
          }
          onSubmit={(value) =>
            manualLoadDefinition.distribution === 'Concentrated'
              ? onUpdate({ ...manualLoadDefinition, coordinate: value })
              : onUpdate({ ...manualLoadDefinition, startCoordinate: value })
          }
          sx={{ m: 1, flex: 1 }}
          disabled={disabled}
          validator={(value) => {
            if (!coordinateIsOnComponent(manualLoadDefinition.definitionAxis, value)) {
              // TODO: why?
              return manualLoadDefinition.distribution === 'Distributed' ? 'not on truss' : '';
            }

            if (
              !distributedManualLoadDefinitionHasDifferentStartAndEnd({
                ...manualLoadDefinition,
                [manualLoadDefinition.distribution === 'Concentrated' ? 'coordinate' : 'startCoordinate']: value,
              })
            ) {
              return 'same as End';
            }
          }}
          canSubmitWhenInvalid={canSubmitWhenInvalid}
        />
        {manualLoadDefinition.distribution === 'Distributed' && (
          <DistanceField
            label="End Location"
            value={manualLoadDefinition.endCoordinate}
            onSubmit={(endCoordinate) => void onUpdate({ ...manualLoadDefinition, endCoordinate })}
            sx={{ m: 1, flex: 1 }}
            disabled={disabled}
            validator={(value) => {
              if (!coordinateIsOnComponent(manualLoadDefinition.definitionAxis, value)) {
                return 'not on truss';
              }

              if (
                !distributedManualLoadDefinitionHasDifferentStartAndEnd({
                  ...manualLoadDefinition,
                  endCoordinate: value,
                })
              ) {
                return 'same as Start';
              }
            }}
            canSubmitWhenInvalid={canSubmitWhenInvalid}
          />
        )}
      </Box>
      <LoadMagnitudeSettings
        loadDistribution={loadDistribution}
        startOrOnlyMagnitude={
          manualLoadDefinition.distribution === 'Concentrated'
            ? manualLoadDefinition.magnitude
            : manualLoadDefinition.startMagnitude
        }
        endMagnitude={
          manualLoadDefinition.distribution === 'Concentrated'
            ? manualLoadDefinition.magnitude
            : manualLoadDefinition.endMagnitude
        }
        magnitudeUnit={
          manualLoadDefinition.distribution === 'Concentrated' ? 'Pounds' : manualLoadDefinition.magnitudeUnit
        }
        component={component}
        submitStartOrOnlyMagnitude={(startOrOnlyMagnitude) => {
          const { distribution } = manualLoadDefinition;
          if (distribution === 'Distributed' && loadDistribution === 'Trapezoidal') {
            onUpdate({
              ...manualLoadDefinition,
              startMagnitude: startOrOnlyMagnitude,
            });
          } else if (distribution === 'Distributed') {
            onUpdate({
              ...manualLoadDefinition,
              startMagnitude: startOrOnlyMagnitude,
              endMagnitude: startOrOnlyMagnitude,
            });
          } else if (distribution === 'Concentrated') {
            onUpdate({ ...manualLoadDefinition, magnitude: startOrOnlyMagnitude });
          } else throw new Error('Unimplemented load magnitude update case');
        }}
        submitEndMagnitude={(endMagnitude) => {
          if (manualLoadDefinition.distribution === 'Concentrated')
            throw new Error('Attempted to update end magnitude on a concentrated manual load');
          onUpdate({ ...manualLoadDefinition, endMagnitude });
        }}
        submitMagnitudeUnit={(magnitudeUnit) => {
          if (
            manualLoadDefinition.distribution === 'Distributed' &&
            (magnitudeUnit === 'PoundsPerLinearFoot' || magnitudeUnit === 'PoundsPerSquareFoot')
          ) {
            onUpdate({ ...manualLoadDefinition, magnitudeUnit });
          } else {
            // TODO there is probably a better way to make TypeScript happy than these conditional blocks
            throw new Error('Illegal magnitude unit for manual load type. This should not happen.');
          }
        }}
        isProjected={manualLoadDefinition.distribution === 'Distributed' ? manualLoadDefinition.isProjected : undefined}
        submitIsProjected={(isProjected) => {
          if (manualLoadDefinition.distribution === 'Distributed') onUpdate({ ...manualLoadDefinition, isProjected });
        }}
        canSubmitWhenInvalid={canSubmitWhenInvalid}
        disabled={disabled}
      />
      <PropertyTextInput
        sx={{ m: 1 }}
        property="Manual Load Name (Optional)"
        value={manualLoadDefinition.name ?? ''}
        submit={(name) => onUpdate({ ...manualLoadDefinition, name })}
        disabled={disabled}
      />
    </>
  );
}
